import { cn } from "@/lib/utils";
import type { ChatMessage as ChatMessageType } from "@/hooks/use-chat-messages";
import { PresenceDot } from "@/components/shared/PresenceDot";
import { SrIcon } from "@/components/ui/SrIcon";

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleString("nb-NO", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function MessageAvatar({
  name,
  url,
  userId,
}: {
  name: string | null;
  url: string | null;
  userId: string | null | undefined;
}) {
  const initials = (name ?? "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div className="relative shrink-0">
      {url ? (
        <img src={url} alt={name ?? ""} className="w-6 h-6 rounded-full object-cover" />
      ) : (
        <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-[10px] font-medium inline-flex items-center justify-center">
          {initials}
        </span>
      )}
      <PresenceDot
        userId={userId}
        size="sm"
        className="absolute -bottom-0.5 -right-0.5"
      />
    </div>
  );
}

function isImage(type: string | null): boolean {
  return !!type && type.startsWith("image/");
}

export function ChatMessage({
  message,
  isSelf,
  showAvatar,
}: {
  message: ChatMessageType;
  isSelf: boolean;
  showAvatar: boolean;
}) {
  return (
    <div className={cn("flex gap-2", isSelf ? "justify-end" : "justify-start")}>
      {!isSelf && (
        <div className="w-6 shrink-0">
          {showAvatar && (
            <MessageAvatar
              name={message.sender?.full_name ?? null}
              url={message.sender?.avatar_url ?? null}
              userId={message.sender_id}
            />
          )}
        </div>
      )}
      <div className={cn("flex flex-col max-w-[78%] space-y-1", isSelf ? "items-end" : "items-start")}>
        {showAvatar && !isSelf && (
          <div className="text-[10px] text-muted-foreground px-1">
            {message.sender?.full_name ?? "Ukjent"}
          </div>
        )}
        {message.attachment_url && (
          <a
            href={message.attachment_url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "block rounded-lg overflow-hidden border",
              isSelf ? "border-primary/30" : "border-border",
            )}
          >
            {isImage(message.attachment_type) ? (
              <img
                src={message.attachment_url}
                alt={message.attachment_name ?? "vedlegg"}
                className="max-h-48 max-w-full object-cover"
              />
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 bg-muted/40 text-sm">
                <SrIcon name="file-text" className="w-4 h-4 text-muted-foreground shrink-0"  />
                <span className="truncate">{message.attachment_name ?? "Vedlegg"}</span>
                <SrIcon name="download" className="w-3.5 h-3.5 text-muted-foreground shrink-0"  />
              </div>
            )}
          </a>
        )}
        {message.body && (
          <div
            className={cn(
              "rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words",
              isSelf ? "bg-primary text-primary-foreground" : "bg-muted",
            )}
          >
            {message.body}
          </div>
        )}
        <div className={cn("text-[10px] text-muted-foreground px-1", isSelf && "text-right")}>
          {formatTime(message.created_at)}
        </div>
      </div>
    </div>
  );
}

export default ChatMessage;
